import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {isClientUser, personIsCollaboratorUser} from '../../forecast-app/shared/util/PermissionsUtil';

type ViewerProfiles = {
	readonly edges: ReadonlyArray<{readonly node: {readonly seat: string | null} | null} | null> | null;
} | null;

/** Gate for the in-app AI agent (Nova): every user of a company with `forecast_agent_chat`
 * enabled may open it, except client users and external collaborators.
 *
 * Deliberately carries no admin check. Nova reads Forecast over the caller's own browser
 * session (the MCP layer authenticates every GraphQL call with the `sessionKey` cookie), so
 * what a user can see through the agent is already bounded by their own permission profile.
 * The former MANAGE_ACCOUNT_SETTINGS check was an early-stage guard, and it silently revoked
 * Nova from anyone whose profile changed from Admin to non-Admin.
 *
 * Enforced in `AgentDrawer`, which the router mounts once for every logged-in user, so every
 * way of reaching Nova goes through it — the nav trigger, and the report-builder "Edit Prompt"
 * banner, whose own `canOpenReportBuilder()` checks flags + INSIGHTS_READ and not a client user.
 * The seat exclusion is this gate's alone, since the builder gate has no viewer profiles to read.
 * `PrimaryNavigationRight` applies it a second time so an excluded user is not
 * shown a button that opens an empty drawer.
 */
export function canUseAgentChat(profiles: ViewerProfiles): boolean {
	if (!hasFeatureFlag('forecast_agent_chat')) {
		return false;
	}
	if (isClientUser()) {
		return false;
	}
	// A person's seat is the seat of their FIRST profile — that is how the platform itself
	// answers the question (PersonType.seat resolves `personProfiles[0].profile.seat`), and both
	// read the same `profilesByPersonId` order. Testing every profile instead would hide Nova
	// from a full-seat user who also holds a collaborator profile, i.e. from someone the rest of
	// the product counts as a full user. The query asks for one edge; tolerate more.
	const firstProfile = (profiles?.edges ?? [])[0]?.node;
	return !firstProfile || !personIsCollaboratorUser(firstProfile);
}
